import { lazy, Suspense, type ComponentType } from "react";
import type { RouteObject } from "react-router-dom";

import { RequireAuth } from "@/auth/RequireAuth";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import type { StoredUser } from "@/auth/storage";

// Login 不做懒加载 —— 未登录时第一个进入的页面，尽量让它立刻可见
import Login from "../pages/login/page";
// NotFound 很小，不做懒加载
import NotFound from "../pages/NotFound";

// 其余路由按需加载，每个页面产物单独成 chunk
const Home = lazy(() => import("../pages/home/page"));
const Tasks = lazy(() => import("../pages/tasks/page"));
const Recite = lazy(() => import("../pages/recite/page"));
const Reading = lazy(() => import("../pages/reading/page"));
const BookDetail = lazy(() => import("../pages/reading/[bookId]/page"));
const MaterialDetail = lazy(() => import("../pages/materials/page"));
const Profile = lazy(() => import("../pages/profile/page"));
const Chat = lazy(() => import("../pages/chat/page"));
const Admin = lazy(() => import("../pages/admin/page"));
const Teacher = lazy(() => import("../pages/teacher/page"));

// 路由 element 统一用 <Lazy /> 组件包裹 lazy 页面，走 Suspense 兜底
// eslint-disable-next-line react-refresh/only-export-components
function Lazy({ component: C }: { component: ComponentType }) {
  return (
    <Suspense
      fallback={
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: "linear-gradient(160deg, #F2F6F0 0%, #E8F0E4 100%)" }}
        >
          <LoadingSpinner size={32} />
        </div>
      }
    >
      <C />
    </Suspense>
  );
}

function ProtectedLazy({
  component,
  roles,
}: {
  component: ComponentType;
  roles?: StoredUser["role"][];
}) {
  return (
    <RequireAuth roles={roles}>
      <Lazy component={component} />
    </RequireAuth>
  );
}

const routes: RouteObject[] = [
  { path: "/login", element: <Login /> },
  { path: "/", element: <ProtectedLazy component={Home} /> },
  { path: "/tasks", element: <ProtectedLazy component={Tasks} /> },
  { path: "/recite", element: <ProtectedLazy component={Recite} /> },
  { path: "/reading", element: <ProtectedLazy component={Reading} /> },
  { path: "/reading/:bookId", element: <ProtectedLazy component={BookDetail} /> },
  { path: "/materials/:id", element: <ProtectedLazy component={MaterialDetail} /> },
  { path: "/profile", element: <ProtectedLazy component={Profile} /> },
  { path: "/chat", element: <ProtectedLazy component={Chat} /> },
  { path: "/admin", element: <ProtectedLazy component={Admin} roles={["admin"]} /> },
  { path: "/teacher", element: <ProtectedLazy component={Teacher} roles={["teacher", "admin"]} /> },
  { path: "*", element: <NotFound /> },
];

export default routes;
